/**
 * Option-order shuffle for quiz attempts / flash decks.
 * Display letters a-d are remapped; scoring uses canonical source answers.
 */

import { shuffleQuestions, type QuizQuestion } from '@/lib/quiz-parser';

export const QUIZ_OPTION_KEYS = ['a', 'b', 'c', 'd'] as const;
export type QuizOptionKey = (typeof QUIZ_OPTION_KEYS)[number];
/** Permutation of canonical option keys in display slots a,b,c,d. */
export type OptionOrder = QuizOptionKey[];

export function shuffleOptionOrder(): OptionOrder {
  return shuffleQuestions([...QUIZ_OPTION_KEYS]) as OptionOrder;
}

/** Build display options from a canonical question + order. */
export function displayOptionsForOrder(
  options: Record<string, string>,
  order: OptionOrder,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < QUIZ_OPTION_KEYS.length; i++) {
    const displayKey = QUIZ_OPTION_KEYS[i];
    const canonical = order[i];
    out[displayKey] = options[canonical] ?? '';
  }
  return out;
}

/** Map a display letter back to the canonical source key. */
export function displayKeyToCanonical(
  displayKey: string,
  order: OptionOrder,
): string {
  const idx = QUIZ_OPTION_KEYS.indexOf(displayKey.toLowerCase() as QuizOptionKey);
  if (idx < 0) return displayKey.toLowerCase();
  return order[idx] || displayKey.toLowerCase();
}

/** Map canonical answer to the display letter for this order. */
export function canonicalToDisplayKey(
  canonicalAnswer: string,
  order: OptionOrder,
): string {
  const canon = String(canonicalAnswer || 'a').toLowerCase();
  const idx = order.indexOf(canon as QuizOptionKey);
  if (idx < 0) return canon;
  return QUIZ_OPTION_KEYS[idx];
}

export function isDisplayChoiceCorrect(
  question: QuizQuestion,
  displayChoice: string,
  order: OptionOrder,
): boolean {
  return (
    displayKeyToCanonical(displayChoice, order) ===
    String(question.answer || 'a').toLowerCase()
  );
}

/** Convert display-letter answers to canonical keys for API submit. */
export function toCanonicalAnswers(
  displayAnswers: Record<string, string>,
  optionOrders: Record<string, OptionOrder>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [qid, displayKey] of Object.entries(displayAnswers)) {
    const order = optionOrders[qid];
    out[qid] = order
      ? displayKeyToCanonical(displayKey, order)
      : String(displayKey).toLowerCase();
  }
  return out;
}

/** Feedback line using the letter the learner actually saw. */
export function formatCorrectAnswerFeedback(
  displayKey: string,
  optionText?: string,
): string {
  const letter = String(displayKey || 'a').toUpperCase();
  const text = String(optionText || '').trim();
  return text ? `Correct answer: ${letter} - ${text}` : `Correct answer: ${letter}`;
}

/**
 * Rewrite common "Option B" / "answer is B" phrases in free-form explanations
 * so they match shuffled display letters (Option B fallback for authored copy).
 */
export function rewriteExplanationForDisplayOrder(
  explanation: string | undefined | null,
  order: OptionOrder,
): string | undefined {
  if (explanation == null || explanation === '') return explanation ?? undefined;
  const canonToDisplay: Record<string, string> = {};
  for (let i = 0; i < QUIZ_OPTION_KEYS.length; i++) {
    canonToDisplay[order[i]] = QUIZ_OPTION_KEYS[i];
  }
  const mapLetter = (letter: string) => {
    const mapped = canonToDisplay[letter.toLowerCase()];
    if (!mapped) return letter;
    return letter === letter.toUpperCase() ? mapped.toUpperCase() : mapped;
  };

  let text = explanation;
  text = text.replace(
    /\b((?:the\s+)?(?:correct\s+)?answer(?:\s+is)?|option|choice)(\s*[:.]?\s*|\s+)(\*\*)?([A-Da-d])(\*\*)?/gi,
    (_full, head: string, sep: string, open: string | undefined, letter: string, close: string | undefined) => {
      const spacer = sep && /\S/.test(sep) ? sep : ' ';
      return `${head}${spacer}${open || ''}${mapLetter(letter)}${close || ''}`;
    },
  );
  // Leading "B -" / "B:" style answer keys
  text = text.replace(
    /^(\*\*)?([A-Da-d])(\*\*)?(\s*[-:]\s+)/,
    (_full, open: string | undefined, letter: string, close: string | undefined, rest: string) =>
      `${open || ''}${mapLetter(letter)}${close || ''}${rest}`,
  );
  return text;
}

/**
 * Remap a question so options/answer use shuffled display letters.
 * Use for flash decks where the client grades against card.answer directly.
 * Also rewrites common letter refs in explanation to the display letter.
 */
export function remapQuestionForDisplay(
  question: QuizQuestion,
  order: OptionOrder = shuffleOptionOrder(),
): QuizQuestion & { optionOrder: OptionOrder } {
  const displayAnswer = canonicalToDisplayKey(question.answer, order);
  const options = displayOptionsForOrder(question.options || {}, order);
  return {
    ...question,
    options,
    answer: displayAnswer,
    explanation: rewriteExplanationForDisplayOrder(question.explanation, order),
    optionOrder: order,
  };
}

export function buildOptionOrdersForQuestions(
  questions: QuizQuestion[],
  shuffle: boolean,
): Record<string, OptionOrder> {
  const orders: Record<string, OptionOrder> = {};
  for (const q of questions) {
    orders[q.id] = shuffle
      ? shuffleOptionOrder()
      : ([...QUIZ_OPTION_KEYS] as OptionOrder);
  }
  return orders;
}
